import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { inr, num } from '../pages/ims/kit/format';

/**
 * Auto-generated approval summary — a WHY / WHERE / WHO block + a per-item
 * table with a resolved "last bought" rate/%-change + deterministic flags
 * (₹0 cost, price drift, PO-vs-GRN match, PO-size outlier, part-repeat on
 * this container). Shared across the PO, Requisition, GRN and Issue Note
 * detail views — see backend/src/services/approval-insight.ts for exactly
 * what is and isn't computed (deliberately no invented reasoning: everything
 * shown traces to a real stored field or a live aggregation).
 *
 * Purely additive: renders nothing while loading and nothing on error/empty
 * so it can never break the page it's dropped into.
 */

interface InsightItem {
  itemName: string;
  qty: number;
  costPerUnit: number;
  lastBought: number | null;
  changePct: number | null;
  lineTotal: number;
}
interface InsightFlag { tone: 'info' | 'warn'; text: string }
interface Insight {
  why: { title: string; sub?: string };
  where: { title: string; sub?: string };
  who: { title: string; sub?: string };
  items: InsightItem[];
  total: number;
  flags: InsightFlag[];
  narrative: string;
  disposition?: {
    lines: Array<{
      ref: string; oldPart: string; qty: number; actionLabel: string;
      accountable: string; status: 'green' | 'amber' | 'red'; statusLabel: string; note: string;
    }>;
    worst: 'green' | 'amber' | 'red';
    openCount: number;
  };
}

const RAG: Record<string, string> = {
  green: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  amber: 'border-amber-200 bg-amber-50 text-amber-900',
  red: 'border-rose-200 bg-rose-50 text-rose-900',
};

export function ApprovalInsightCard({ docType, id }: { docType: 'po' | 'requisition' | 'grn' | 'issue'; id: string }) {
  const { data, isError } = useQuery<Insight>({
    queryKey: ['approval-insight', docType, id],
    queryFn: () => api.get<Insight>(`/api/approval-insight/${docType}/${id}`),
    enabled: !!id,
    staleTime: 60_000,
    retry: false,
  });

  if (isError || !data) return null;

  const facts: Array<{ label: string; v: { title: string; sub?: string } }> = [
    { label: 'Why', v: data.why },
    { label: 'Where', v: data.where },
    { label: 'Who', v: data.who },
  ];

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/60">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Summary — auto-generated from purchase &amp; container history</div>
      </div>
      {data.narrative && (
        <div className="mx-3 mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-slate-700">
          {data.narrative}
        </div>
      )}
      <div className="p-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {facts.map(({ label, v }) => (
          <div key={label}>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">{label}</div>
            <div className="text-sm text-slate-800 mt-0.5">{v.title}</div>
            {v.sub && <div className="text-[11px] text-slate-500 mt-0.5">{v.sub}</div>}
          </div>
        ))}
      </div>
      {data.items.length > 0 && (
        <div className="px-3 pb-3 overflow-x-auto">
          <table className="grid-table">
            <thead>
              <tr>
                <th>Item</th>
                <th className="num">Qty</th>
                <th className="num">Cost / unit</th>
                <th className="num">Last bought</th>
                <th className="num">Change</th>
                <th className="num">Line total</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it, i) => (
                <tr key={i}>
                  <td>{it.itemName}</td>
                  <td className="num">{num(it.qty)}</td>
                  <td className="num">{inr(it.costPerUnit)}</td>
                  <td className="num text-slate-500">{it.lastBought != null ? inr(it.lastBought) : '—'}</td>
                  <td className={`num ${it.changePct == null ? 'text-slate-400' : it.changePct > 0 ? 'text-rose-700 font-medium' : 'text-emerald-700 font-medium'}`}>
                    {it.changePct != null ? `${it.changePct > 0 ? '▲' : '▼'} ${Math.abs(it.changePct).toFixed(1)}%` : '—'}
                  </td>
                  <td className="num font-medium">{inr(it.lineTotal)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <td colSpan={5} className="num">Total</td>
                <td className="num">{inr(data.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {/*
        WHAT HAPPENED TO THE LAST ONE.
        Approving a replacement without knowing whether the part it replaces was
        ever returned, scrapped or accounted for is how a container quietly
        collects three of the same unit. Only the OPEN ones are surfaced here —
        a settled disposition is not something an approver needs to read.
      */}
      {!!data.disposition?.openCount && (
        <div className="px-3 pb-3">
          <div className={`rounded-lg border p-2.5 ${RAG[data.disposition.worst]}`}>
            <div className="text-[11px] font-bold uppercase tracking-wide">
              {data.disposition.openCount} old part{data.disposition.openCount === 1 ? '' : 's'} still unaccounted on this job
            </div>
            {data.disposition.lines.filter((l) => l.status !== 'green').slice(0, 4).map((l) => (
              <div key={l.ref} className="mt-1 text-[11.5px] leading-relaxed">
                <b>{l.oldPart}</b>{l.qty ? ` x${l.qty}` : ''} — {l.actionLabel}
                {l.accountable ? ` · ${l.accountable}` : ''}
                <span className="ml-1 font-semibold">[{l.statusLabel}]</span>
                <div className="opacity-80">{l.note}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {data.flags.length > 0 && (
        <div className="px-3 pb-3 space-y-1.5">
          {data.flags.map((f, i) => (
            <div
              key={i}
              className={`rounded-lg border px-3 py-2 text-xs ${f.tone === 'warn' ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}
            >
              {f.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
